/**
 * Dependent contract — mirrors `platform.employee_dependents`.
 *
 * Used by payroll + benefits modules (tax deductions, insurance, gratuity
 * nomination). `dependentNominee=true` flags the row as eligible for
 * payouts; HR policy (not the DB) enforces how many nominees a record
 * should carry.
 */
import { z } from 'zod';

import { TenantIdSchema } from '../common/identifiers.js';
import { AuditFieldsSchema } from '../common/timestamps.js';

import { EmployeeIdSchema } from './employee.js';

export const DependentIdSchema = z.string().uuid().brand<'DependentId'>();
export type DependentId = z.infer<typeof DependentIdSchema>;

export const DependentRelationshipSchema = z.enum([
  'spouse',
  'child',
  'parent',
  'sibling',
  'other',
]);
export type DependentRelationship = z.infer<typeof DependentRelationshipSchema>;

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected ISO date YYYY-MM-DD');

export const EmployeeDependentSchema = AuditFieldsSchema.extend({
  id: DependentIdSchema,
  tenantId: TenantIdSchema,
  employeeId: EmployeeIdSchema,
  name: z.string().min(1).max(200),
  relationship: DependentRelationshipSchema,
  dateOfBirth: IsoDateSchema.nullable(),
  dependentNominee: z.boolean(),
});
export type EmployeeDependent = z.infer<typeof EmployeeDependentSchema>;

/** Input for `POST /employees/:id/dependents`. */
export const NewEmployeeDependentSchema = z.object({
  name: z.string().min(1).max(200),
  relationship: DependentRelationshipSchema,
  dateOfBirth: IsoDateSchema.nullable().optional(),
  dependentNominee: z.boolean().optional(),
});
export type NewEmployeeDependent = z.infer<typeof NewEmployeeDependentSchema>;

/** Input for `PATCH /employees/:id/dependents/:dependentId`. */
export const UpdateEmployeeDependentSchema = NewEmployeeDependentSchema.partial();
export type UpdateEmployeeDependent = z.infer<typeof UpdateEmployeeDependentSchema>;
